import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";

const cls = (...xs) => xs.filter(Boolean).join(" ");

const StatusBadge = ({ status }) => {
    const c = {
        healthy: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40",
        degraded: "bg-amber-500/15 text-amber-500 border-amber-500/40",
        critical: "bg-red-500/15 text-red-500 border-red-500/40",
    }[status] || "bg-slate-500/15 text-slate-500 border-slate-500/40";
    const label = {
        healthy: "All Systems Operational",
        degraded: "Degraded",
        critical: "Critical",
    }[status] || "Unknown";
    return (
        <span
            data-testid="health-status-badge"
            className={cls("px-3 py-1 text-sm font-medium border rounded-full", c)}
        >
            {label}
        </span>
    );
};

const Metric = ({ label, value, unit, thresholds, testId }) => {
    // thresholds: { warn: number, crit: number } — colors the value
    let color = "text-emerald-500";
    if (thresholds && typeof value === "number") {
        if (thresholds.crit != null && value >= thresholds.crit) color = "text-red-500";
        else if (thresholds.warn != null && value >= thresholds.warn) color = "text-amber-500";
    }
    return (
        <div
            className="rounded-xl border border-border/60 p-4"
            data-testid={testId}
        >
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className={cls("mt-1 text-2xl font-semibold", color)}>
                {value == null || value === "" ? "—" : value}
                {unit ? <span className="text-sm font-normal text-muted-foreground ml-1">{unit}</span> : null}
            </p>
        </div>
    );
};

const fmtSecs = (s) => {
    if (s == null) return "—";
    if (s < 60) return `${Math.round(s)}s`;
    if (s < 3600) return `${Math.round(s / 60)}m`;
    return `${(s / 3600).toFixed(1)}h`;
};

const fmtTs = (t) => {
    if (!t) return "";
    try {
        const d = new Date(t * 1000);
        return d.toLocaleTimeString();
    } catch {
        return "";
    }
};

export default function SystemHealthTab() {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [lastFetchedAt, setLastFetchedAt] = useState(null);

    const fetchHealth = useCallback(async () => {
        try {
            const res = await api.get(`/admin/health`);
            setData(res.data);
            setErr(null);
            setLastFetchedAt(new Date());
        } catch (e) {
            setErr(e?.response?.data?.detail || e.message);
        }
    }, []);

    useEffect(() => {
        fetchHealth();
    }, [fetchHealth]);

    useEffect(() => {
        if (!autoRefresh) return undefined;
        const id = setInterval(fetchHealth, 10_000);
        return () => clearInterval(id);
    }, [autoRefresh, fetchHealth]);

    if (err) {
        return (
            <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-6">
                <p className="text-red-500 font-medium">Failed to load system health</p>
                <p className="text-sm text-muted-foreground mt-2">{err}</p>
                <button
                    onClick={fetchHealth}
                    className="mt-3 px-3 py-1.5 text-sm rounded-lg bg-primary text-primary-foreground"
                    data-testid="health-retry-btn"
                >
                    Retry
                </button>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="rounded-xl border border-border/60 p-6" data-testid="health-loading">
                <p className="text-muted-foreground">Loading system health…</p>
            </div>
        );
    }

    const t = data.traffic || {};
    const lat = t.api_latency_ms || {};
    const mongoLat = data.mongo?.latency_ms;
    const memPct = data.pod?.memory_pct;

    return (
        <div className="space-y-6" data-testid="admin-health-tab">
            {/* Top status banner */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <StatusBadge status={data.status} />
                    {data.reasons?.length ? (
                        <span className="text-sm text-muted-foreground">
                            ({data.reasons.join(", ")})
                        </span>
                    ) : null}
                </div>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span data-testid="health-last-updated">
                        Updated {lastFetchedAt ? lastFetchedAt.toLocaleTimeString() : "…"}
                    </span>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={autoRefresh}
                            onChange={(e) => setAutoRefresh(e.target.checked)}
                            data-testid="health-autorefresh-toggle"
                        />
                        Auto-refresh (10s)
                    </label>
                    <button
                        onClick={fetchHealth}
                        className="px-3 py-1 rounded-lg border border-border/60 hover:bg-accent/40"
                        data-testid="health-refresh-btn"
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {/* Traffic-light metrics grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Metric
                    label="API p50 latency"
                    value={lat.p50}
                    unit="ms"
                    thresholds={{ warn: 300, crit: 1000 }}
                    testId="metric-p50"
                />
                <Metric
                    label="API p95 latency"
                    value={lat.p95}
                    unit="ms"
                    thresholds={{ warn: 800, crit: 2000 }}
                    testId="metric-p95"
                />
                <Metric
                    label="MongoDB ping"
                    value={mongoLat}
                    unit="ms"
                    thresholds={{ warn: 50, crit: 200 }}
                    testId="metric-mongo"
                />
                <Metric
                    label="Requests / min"
                    value={t.requests_per_min}
                    testId="metric-rpm"
                />
                <Metric
                    label="Active users (60s)"
                    value={t.active_user_ids}
                    testId="metric-active"
                />
                <Metric
                    label="Error rate"
                    value={t.error_rate_pct}
                    unit="%"
                    thresholds={{ warn: 1, crit: 5 }}
                    testId="metric-errors"
                />
                <Metric
                    label="Pod memory"
                    value={memPct}
                    unit="%"
                    thresholds={{ warn: 70, crit: 90 }}
                    testId="metric-memory"
                />
                <Metric
                    label="Uptime"
                    value={fmtSecs(t.uptime_seconds)}
                    testId="metric-uptime"
                />
            </div>

            {/* Secondary info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="rounded-xl border border-border/60 p-4" data-testid="health-mongo-panel">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">MongoDB</p>
                    <div className="mt-2 space-y-1">
                        <p>Status: {data.mongo?.ok ? "🟢 Reachable" : "🔴 Unreachable"}</p>
                        <p>Latency: {mongoLat != null ? `${mongoLat} ms` : "—"}</p>
                        <p>maxPoolSize: {data.mongo?.pool?.max_pool_size ?? "—"}</p>
                    </div>
                </div>
                <div className="rounded-xl border border-border/60 p-4" data-testid="health-pod-panel">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Pod</p>
                    <div className="mt-2 space-y-1">
                        <p>Memory: {data.pod?.memory_mb != null ? `${data.pod.memory_mb} MB` : "—"} ({memPct ?? "—"}%)</p>
                        <p>CPU (last sample): {data.pod?.cpu_pct != null ? `${data.pod.cpu_pct}%` : "—"}</p>
                    </div>
                </div>
                <div className="rounded-xl border border-border/60 p-4" data-testid="health-traffic-panel">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">60s Window</p>
                    <div className="mt-2 space-y-1">
                        <p>Total requests: {t.total_requests}</p>
                        <p>Errors: {t.error_count}</p>
                        <p>API p99: {lat.p99} ms</p>
                        <p>API max: {lat.max} ms</p>
                    </div>
                </div>
            </div>

            {/* Growth signals */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Metric
                    label="Total registrations"
                    value={data.growth?.total_users}
                    testId="metric-total-users"
                />
                <Metric
                    label="DAU (last 24 h)"
                    value={data.growth?.dau}
                    testId="metric-dau"
                />
                <Metric
                    label="MAU (last 30 d)"
                    value={data.growth?.mau}
                    testId="metric-mau"
                />
            </div>

            {/* Capacity Advisor — plain-English recommendations */}
            {(data.advisor || []).length > 0 && (
                <div className="rounded-xl border border-border/60" data-testid="capacity-advisor">
                    <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
                        <p className="font-medium">Capacity Advisor</p>
                        <p className="text-xs text-muted-foreground">
                            What to upgrade next, based on your traffic
                        </p>
                    </div>
                    <ul className="divide-y divide-border/60">
                        {data.advisor.map((a, i) => {
                            const sev = a.severity || "ok";
                            const dot = {
                                ok: "bg-emerald-500",
                                warn: "bg-amber-500",
                                critical: "bg-red-500",
                            }[sev] || "bg-slate-500";
                            const label = {
                                ok: "OK",
                                warn: "Recommended",
                                critical: "URGENT",
                            }[sev] || "Info";
                            const labelCls = {
                                ok: "bg-emerald-500/15 text-emerald-500 border-emerald-500/40",
                                warn: "bg-amber-500/15 text-amber-500 border-amber-500/40",
                                critical: "bg-red-500/15 text-red-500 border-red-500/40",
                            }[sev] || "bg-slate-500/15 text-slate-500";
                            return (
                                <li
                                    key={i}
                                    className="px-4 py-3 space-y-1"
                                    data-testid={`advisor-item-${a.target || i}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className={cls("inline-block w-2 h-2 rounded-full", dot)} />
                                        <p className="font-medium">{a.title}</p>
                                        <span
                                            className={cls(
                                                "ml-auto px-2 py-0.5 text-xs font-medium border rounded-full",
                                                labelCls,
                                            )}
                                        >
                                            {label}
                                        </span>
                                    </div>
                                    {a.reason ? (
                                        <p className="text-sm text-muted-foreground pl-4">
                                            {a.reason}
                                        </p>
                                    ) : null}
                                    {a.action && sev !== "ok" ? (
                                        <p className="text-sm pl-4">
                                            <span className="text-xs uppercase tracking-wide text-muted-foreground mr-1">
                                                Action:
                                            </span>
                                            {a.action}
                                        </p>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {/* Recent errors */}
            <div className="rounded-xl border border-border/60" data-testid="health-recent-errors">
                <div className="px-4 py-3 border-b border-border/60 flex items-center justify-between">
                    <p className="font-medium">Recent 5xx errors</p>
                    <p className="text-xs text-muted-foreground">Last 10</p>
                </div>
                {(data.recent_errors || []).length === 0 ? (
                    <p className="px-4 py-6 text-sm text-emerald-500">
                        ✓ No recent errors
                    </p>
                ) : (
                    <ul className="divide-y divide-border/60 text-sm">
                        {data.recent_errors.map((e, i) => (
                            <li key={i} className="px-4 py-2 flex flex-wrap gap-x-4 gap-y-1">
                                <span className="text-red-500 font-mono">HTTP {e.status}</span>
                                <span className="font-mono text-muted-foreground">{e.path}</span>
                                <span className="text-xs text-muted-foreground">{e.duration_ms}ms</span>
                                <span className="text-xs text-muted-foreground ml-auto">{fmtTs(e.ts)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Reference thresholds */}
            <details className="text-sm text-muted-foreground">
                <summary className="cursor-pointer">Threshold reference (what triggers amber / red)</summary>
                <ul className="mt-2 ml-5 list-disc space-y-1">
                    <li>API p50 &gt; 300 ms → amber, &gt; 1000 ms → red</li>
                    <li>API p95 &gt; 800 ms → amber, &gt; 2000 ms → red</li>
                    <li>MongoDB ping &gt; 50 ms → amber, &gt; 200 ms → red (upgrade Mongo tier)</li>
                    <li>Error rate &gt; 1 % → amber, &gt; 5 % → red</li>
                    <li>Pod memory &gt; 70 % → amber, &gt; 90 % → red (upgrade compute tier)</li>
                </ul>
                <p className="mt-3 font-medium text-foreground">Capacity Advisor rules</p>
                <ul className="mt-1 ml-5 list-disc space-y-1">
                    <li>DAU ≥ 500 AND MongoDB pool &lt; 100 → recommend maxPoolSize = 100</li>
                    <li>MongoDB ping &gt; 80 ms OR total users ≥ 5,000 → recommend dedicated Mongo cluster</li>
                    <li>Memory ≥ 75 % OR CPU ≥ 70 % OR MAU ≥ 5,000 → upgrade compute Grow → Scale</li>
                    <li>MAU ≥ 10,000 OR memory ≥ 85 % → upgrade compute Grow → Elite</li>
                </ul>
            </details>
        </div>
    );
}
